import {
  Box,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  IconButton,
  Text,
  useBreakpointValue,
} from "@chakra-ui/react";
import { ArrowBackIcon } from "@chakra-ui/icons";
import { useMemo } from "react";
import { useMessagesTab } from "../hooks";
import AttachmentsChips from "./messages/AttachmentsChips";
import ChatHeader from "./messages/ChatHeader";
import ChatPlaceholder from "./messages/ChatPlaceholder";
import MessageComposer from "./messages/MessageComposer";
import MessagesList from "./messages/MessagesList";
import SupportChatsPanel from "./messages/SupportChatsPanel";

export default function MessagesTab() {
  const autoSelectFirstChat = useBreakpointValue({ base: false, md: true });
  const isMobile = useBreakpointValue({ base: true, md: false });
  const {
    chatItems,
    isChatsLoading,
    chatsMeta,
    chatsPage,
    setChatsPage,
    selectedChatId,
    setSelectedChatId,
    selectedChat,
    selectedChatTitle,
    selectedChatDisplayDate,
    selectedChatStatus,
    messages,
    isMessagesLoading,
    onRetrySend,
    scrollToBottomKey,
    setIsAtBottom,
    onLoadOlder,
    isLoadingOlder,
    hasMoreOlder,
    currentCustomerId,
    draft,
    setDraft,
    attachedFiles,
    isUploading,
    openFilePicker,
    removeFile,
    isSending,
    onSend,
  } = useMessagesTab({ autoSelectFirstChat: autoSelectFirstChat ?? true });

  const showMessages = selectedChatId != null;

  const selectedChatOrderId = useMemo(() => {
    const raw =
      (selectedChat as any)?.order_id ??
      (selectedChat as any)?.orderId ??
      (selectedChat as any)?.order?.order_id ??
      (selectedChat as any)?.order?.id ??
      null;
    return raw != null && Number(raw) !== 0 ? String(raw) : undefined;
  }, [selectedChat]);

  return (
    <Box
      w="100%"
      minH={{ base: "auto", md: "741px" }}
      h={{ base: "auto", md: "741px" }}
      p={{ base: "0", md: "24px" }}
      bg={{ base: "transparent", md: "#F5F7FA" }}
      border={{ base: "none", md: "1px solid #E6ECF3" }}
      borderRadius={{ base: "0", md: "12px" }}
      overflow="hidden"
    >
      {/* Desktop layout */}
      <Flex
        display={{ base: "none", md: "flex" }}
        gap="24px"
        direction="row"
        h="100%"
        align="stretch"
      >
        <Box h="100%">
          <SupportChatsPanel
            title="Support requests"
            isLoading={isChatsLoading}
            items={chatItems}
            meta={chatsMeta}
            currentPage={chatsPage}
            onChangePage={setChatsPage}
            selectedChatId={selectedChatId}
            onSelect={setSelectedChatId}
          />
        </Box>

        <Box
          flex="1"
          bg="#FFFFFF"
          border="1px solid #E6ECF3"
          borderRadius="12px"
          h="100%"
          maxH="100%"
          p="24px"
          overflow="hidden"
          display="flex"
          flexDirection="column"
        >
          {selectedChatId == null ? (
            <ChatPlaceholder title="You have no support history.  Once you contact us, your conversations will appear here." />
          ) : (
            <Flex direction="column" flex="1" minH={0} overflow="hidden">
              <ChatHeader
                title={selectedChatTitle}
                displayDate={selectedChatDisplayDate}
                status={selectedChatStatus}
                orderId={selectedChatOrderId}
              />

              <MessagesList
                isLoading={isMessagesLoading}
                messages={messages ?? []}
                currentCustomerId={currentCustomerId}
                onRetrySend={onRetrySend}
                scrollToBottomKey={scrollToBottomKey}
                onAtBottomChange={setIsAtBottom}
                onLoadOlder={onLoadOlder}
                isLoadingOlder={isLoadingOlder}
                hasMoreOlder={hasMoreOlder}
              />

              {selectedChatStatus !== "Closed" && (
                <>
                  <AttachmentsChips files={attachedFiles} onRemove={removeFile} />

                  <MessageComposer
                    draft={draft}
                    onDraftChange={setDraft}
                    onAttach={openFilePicker}
                    isUploading={isUploading}
                    onSend={onSend}
                    isSending={isSending}
                  />
                </>
              )}
            </Flex>
          )}
        </Box>
      </Flex>

      {/* Mobile layout - chat list */}
      <Box display={{ base: "block", md: "none" }}>
        <SupportChatsPanel
          title="Support requests"
          isLoading={isChatsLoading}
          items={chatItems}
          meta={chatsMeta}
          currentPage={chatsPage}
          onChangePage={setChatsPage}
          selectedChatId={selectedChatId}
          onSelect={setSelectedChatId}
        />
      </Box>

      {/* Mobile chat Drawer */}
      <Drawer
        isOpen={showMessages && isMobile}
        placement="right"
        onClose={() => setSelectedChatId(null as any)}
        size="full"
      >
        <DrawerOverlay display={{ base: "block", md: "none" }} />
        <DrawerContent display={{ base: "flex", md: "none" }}>
          <DrawerHeader
            p="0"
            bg="#F5F7FA"
          >
            <Flex align="center" justify="center" position="relative" h="70px" px="16px">
              <IconButton
                aria-label="Back"
                icon={<ArrowBackIcon boxSize="20px" />}
                variant="ghost"
                size="md"
                position="absolute"
                left="8px"
                onClick={() => setSelectedChatId(null as any)}
              />
              <Text
                fontSize="20px"
                fontWeight="600"
                color="#0B0E10"
                textAlign="center"
              >
                {selectedChatOrderId ? `Order #${selectedChatOrderId}` : selectedChatTitle}
              </Text>
              <Box
                position="absolute"
                right="16px"
                px="14px"
                py="8px"
                borderRadius="8px"
                fontSize="14px"
                fontWeight="500"
                bg={selectedChatStatus === "Closed" ? "#9CA3AF" : "#BFD7FF"}
                color={selectedChatStatus === "Closed" ? "#FFF" : "#0B0E10"}
              >
                {selectedChatStatus}
              </Box>
            </Flex>
          </DrawerHeader>

          <DrawerBody
            p="0"
            display="flex"
            flexDirection="column"
            overflow="hidden"
          >
            {/* Messages area - scrollable */}
            <Box flex="1" overflow="hidden" display="flex" flexDirection="column" px="12px">
              <MessagesList
                isLoading={isMessagesLoading}
                messages={messages ?? []}
                currentCustomerId={currentCustomerId}
                onRetrySend={onRetrySend}
                scrollToBottomKey={scrollToBottomKey}
                onAtBottomChange={setIsAtBottom}
                onLoadOlder={onLoadOlder}
                isLoadingOlder={isLoadingOlder}
                hasMoreOlder={hasMoreOlder}
              />
            </Box>

          </DrawerBody>

          {/* Fixed bottom - attachments and composer (hidden for closed chats) */}
          {selectedChatStatus !== "Closed" && (
            <DrawerFooter
              p="0"
              borderTop="1px solid #E6ECF3"
              bg="#FFFFFF"
            >
              <Box w="100%" px="12px" py="12px">
                <AttachmentsChips files={attachedFiles} onRemove={removeFile} />
                <MessageComposer
                  draft={draft}
                  onDraftChange={setDraft}
                  onAttach={openFilePicker}
                  isUploading={isUploading}
                  onSend={onSend}
                  isSending={isSending}
                />
              </Box>
            </DrawerFooter>
          )}
        </DrawerContent>
      </Drawer>
    </Box>
  );
}
